import React, { useState, memo } from 'react';
import { Star, Mail, Loader2 } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

export const Freebie = memo(function Freebie({ storefrontSettings, onSubmit }) {
    const [localFormData, setLocalFormData] = useState({ name: '', email: '' });
    const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
    const [freebieRef, freebieRevealed] = useScrollReveal({ threshold: 0.15 });
    const [formRef, formRevealed] = useScrollReveal({ threshold: 0.15 });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingLocal(true);
        try {
            await onSubmit?.(localFormData.name, localFormData.email);
        } finally {
            setIsSubmittingLocal(false);
        }
    };

    return (
        <section id="freebie" className="py-24 bg-[#3d3d3d] text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                {/* Texture overlay placeholder */}
                <div className="w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]"></div>
            </div>

            <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
                <div ref={freebieRef} className={freebieRevealed ? 'reveal revealed' : 'reveal'}>
                    <Star className="text-[#c5a059] w-12 h-12 mx-auto mb-6 float" />
                    <h2 className="text-4xl font-serif mb-4 text-[#fdfbf7]">
                        {storefrontSettings?.freebieTitle || 'Free Guided Meditation:'} <br />
                        <span className="text-[#d48c95] gradient-text">{storefrontSettings?.freebieTitleHighlight || '"Return to Self"'}</span>
                    </h2>
                    {storefrontSettings?.freebieDescription && (
                        <p className="text-gray-300 mb-10 max-w-xl mx-auto text-lg leading-relaxed">
                            {storefrontSettings.freebieDescription}
                        </p>
                    )}
                </div>

                <form ref={formRef} onSubmit={handleSubmit} className={`max-w-md mx-auto bg-white/5 p-8 rounded-2xl border border-white/10 backdrop-blur-sm hover-glow ${formRevealed ? 'reveal revealed reveal-delay-1' : 'reveal'}`}>
                    <div className="space-y-4">
                        <input
                            required
                            type="text"
                            placeholder="Your First Name"
                            className="w-full px-5 py-3 rounded-lg bg-white/90 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all duration-200 focus:scale-[1.01]"
                            value={localFormData.name}
                            onChange={(e) => setLocalFormData(prev => ({ ...prev, name: e.target.value }))}
                        />
                        <input
                            required
                            type="email"
                            placeholder="Your Email Address"
                            className="w-full px-5 py-3 rounded-lg bg-white/90 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition-all duration-200 focus:scale-[1.01]"
                            value={localFormData.email}
                            onChange={(e) => setLocalFormData(prev => ({ ...prev, email: e.target.value }))}
                        />
                        <button
                            type="submit"
                            disabled={isSubmittingLocal}
                            className="w-full py-4 sm:py-3.5 bg-[#d48c95] hover:bg-[#c07b83] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center min-h-[52px] text-base hover-lift button-press focus-ring group"
                        >
                            {isSubmittingLocal ? (
                                <>
                                    <Loader2 size={18} className="mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    {storefrontSettings?.freebieButtonText || 'Send Me The Meditation'}
                                    <Mail size={18} className="ml-2 group-hover:scale-110 transition-transform" />
                                </>
                            )}
                        </button>
                    </div>
                    {storefrontSettings?.freebieDisclaimer && (
                        <p className="text-xs text-gray-400 mt-4">
                            {storefrontSettings.freebieDisclaimer}
                        </p>
                    )}
                </form>
            </div>
        </section>
    );
});

export default Freebie;
